// Lista de ítems del checklist de Modo Auditor — componente compartido entre
// el autochequeo del admin (AuditoriaPage) y la vista guiada del inspector
// (ChecklistAuditoriaPage): mismo dato, mismo layout tablet-first (una sola
// columna, targets grandes), la única diferencia entre ambos consumidores es
// quién dispara la llamada y qué acciones rodean la lista.
//
// "Ver evidencia" (2026-09-09, pedido tras probar el checklist en vivo): el
// dato real detrás del `detalle` de cada ítem, plegado por defecto — un
// tarjeta clickeable, no un modal, para que el inspector no pierda el lugar
// en la lista. Los ítems sin `evidencia` (ej. tipologia-retencion) no son
// clickeables — nada que expandir.
import type { JSX } from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { notify } from '@/components/ui/sonner';
import { descargarAccesosCsv, descargarCertificadoDestruccion, descargarEntregaCertificado, descargarFichaLote, descargarFirmasCsv, type ChecklistAuditoria, type EstadoItemChecklist } from '@/lib/hceAuditoria';

/** Exportado (2026-09-10): lo reusa admin/IndiceCompletitudHc.tsx (detalle por paciente de calidadHc) — mismos 4 estados, mismo criterio de color, sin duplicar el mapeo. */
export const VARIANTE_POR_ESTADO: Record<EstadoItemChecklist, 'sage' | 'clay' | 'destructive' | 'outline'> = {
  bien: 'sage',
  atencion: 'clay',
  critico: 'destructive',
  informativo: 'outline',
};

/**
 * `token` opcional (2026-09-10) — sin él (por ejemplo si algún consumidor
 * futuro no lo tiene a mano) el componente sigue funcionando igual, solo sin
 * los botones de descarga de este par de ítems puntuales
 * ("registro-accesos", "destruccion-certificada"): son los únicos que
 * necesitan un fetch autenticado aparte, el resto de la evidencia (`href`)
 * no lo requiere.
 */
export function ChecklistItems({ checklist, token }: { checklist: ChecklistAuditoria; token?: string }): JSX.Element {
  const { t } = useTranslation();
  const [expandido, setExpandido] = useState<string | null>(null);
  const [descargando, setDescargando] = useState<string | null>(null);

  async function descargarCsvAccesos(): Promise<void> {
    if (!token) return;
    setDescargando('registro-accesos');
    try {
      await descargarAccesosCsv(token);
    } catch (err) {
      notify({ color: 'red', message: err instanceof Error ? err.message : String(err) });
    } finally {
      setDescargando(null);
    }
  }

  async function descargarCertificado(reference: string): Promise<void> {
    if (!token) return;
    setDescargando(reference);
    try {
      await descargarCertificadoDestruccion(token, reference);
    } catch (err) {
      notify({ color: 'red', message: err instanceof Error ? err.message : String(err) });
    } finally {
      setDescargando(null);
    }
  }

  async function descargarFicha(valor: string, lotId: string): Promise<void> {
    if (!token) return;
    setDescargando(valor);
    try {
      await descargarFichaLote(token, lotId);
    } catch (err) {
      notify({ color: 'red', message: err instanceof Error ? err.message : String(err) });
    } finally {
      setDescargando(null);
    }
  }

  async function descargarCertificadoEntrega(valor: string, entregaId: string): Promise<void> {
    if (!token) return;
    setDescargando(valor);
    try {
      await descargarEntregaCertificado(token, entregaId);
    } catch (err) {
      notify({ color: 'red', message: err instanceof Error ? err.message : String(err) });
    } finally {
      setDescargando(null);
    }
  }

  async function descargarCsvFirmas(): Promise<void> {
    if (!token) return;
    setDescargando('firma-electronica');
    try {
      await descargarFirmasCsv(token);
    } catch (err) {
      notify({ color: 'red', message: err instanceof Error ? err.message : String(err) });
    } finally {
      setDescargando(null);
    }
  }

  return (
    <div className="space-y-2">
      {checklist.items.map((item) => {
        const clickeable = Boolean(item.evidencia);
        const abierto = expandido === item.clave;
        return (
          <div key={item.clave} className="rounded-lg border border-border bg-hce-surface overflow-hidden">
            <button
              type="button"
              disabled={!clickeable}
              onClick={() => setExpandido(abierto ? null : item.clave)}
              className={`w-full text-left p-3 ${clickeable ? 'cursor-pointer hover:bg-hce-paper' : 'cursor-default'}`}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="text-sm font-medium text-hce-ink">{item.titulo}</div>
                  <div className="text-xs font-mono text-hce-muted mt-0.5">{item.cita}</div>
                </div>
                <Badge variant={VARIANTE_POR_ESTADO[item.estado]}>{t(`auditoria.estado.${item.estado}`)}</Badge>
              </div>
              <p className="text-sm text-hce-muted mt-2">{item.detalle}</p>
              {clickeable && <p className="text-xs text-hce-pine mt-1 font-medium">{abierto ? t('auditoria.ocultarEvidencia') : t('auditoria.verEvidencia')}</p>}
            </button>

            {abierto && item.evidencia && (
              <div className="border-t border-border bg-hce-paper px-3 py-2">
                {item.clave === 'registro-accesos' && token && (
                  <Button size="sm" variant="outline" className="mb-2" disabled={descargando === 'registro-accesos'} onClick={() => void descargarCsvAccesos()}>
                    {descargando === 'registro-accesos' ? t('auditoria.descargando') : t('auditoria.descargarAccesosCsv')}
                  </Button>
                )}
                {item.clave === 'firma-electronica' && token && (
                  <Button size="sm" variant="outline" className="mb-2" disabled={descargando === 'firma-electronica'} onClick={() => void descargarCsvFirmas()}>
                    {descargando === 'firma-electronica' ? t('auditoria.descargando') : t('auditoria.descargarFirmasCsv')}
                  </Button>
                )}
                {'texto' in item.evidencia ? (
                  <p className="text-sm text-hce-ink">{item.evidencia.texto}</p>
                ) : (
                  <dl className="space-y-1">
                    {item.evidencia.filas.map((fila, i) => (
                      <div key={i} className="flex items-baseline justify-between gap-3 text-sm">
                        <dt className="text-hce-muted flex items-center gap-2">
                          {fila.href ? (
                            <a href={fila.href} target="_blank" rel="noreferrer" className="text-hce-pine underline-offset-2 hover:underline">
                              {fila.label}
                            </a>
                          ) : (
                            fila.label
                          )}
                          {item.clave === 'destruccion-certificada' && token && fila.label && (
                            <button
                              type="button"
                              className="text-xs text-hce-pine underline-offset-2 hover:underline"
                              disabled={descargando === fila.label}
                              onClick={() => void descargarCertificado(fila.label)}
                            >
                              {descargando === fila.label ? t('auditoria.descargando') : t('auditoria.descargarPdf')}
                            </button>
                          )}
                          {item.clave === 'inventario-trazabilidad-sanitaria' && token && /#(\d+)$/.test(fila.valor) && (
                            <button
                              type="button"
                              className="text-xs text-hce-pine underline-offset-2 hover:underline"
                              disabled={descargando === fila.valor}
                              onClick={() => void descargarFicha(fila.valor, fila.valor.match(/#(\d+)$/)![1])}
                            >
                              {descargando === fila.valor ? t('auditoria.descargando') : t('auditoria.descargarPdf')}
                            </button>
                          )}
                          {item.clave === 'entregas-gestor-desechos-periodo' && token && fila.valor.startsWith('Certificado entrega #') && (
                            <button
                              type="button"
                              className="text-xs text-hce-pine underline-offset-2 hover:underline"
                              disabled={descargando === fila.valor}
                              onClick={() => void descargarCertificadoEntrega(fila.valor, fila.valor.match(/#(\d+)$/)![1])}
                            >
                              {descargando === fila.valor ? t('auditoria.descargando') : t('auditoria.descargarPdf')}
                            </button>
                          )}
                          {item.clave === 'entregas-gestor-desechos-periodo' && token && fila.valor.startsWith('Ficha #') && (
                            <button
                              type="button"
                              className="text-xs text-hce-pine underline-offset-2 hover:underline"
                              disabled={descargando === fila.valor}
                              onClick={() => void descargarFicha(fila.valor, fila.valor.match(/#(\d+)$/)![1])}
                            >
                              {descargando === fila.valor ? t('auditoria.descargando') : t('auditoria.descargarPdf')}
                            </button>
                          )}
                        </dt>
                        <dd className="font-mono text-hce-ink text-right">{fila.valor}</dd>
                      </div>
                    ))}
                  </dl>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
